import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { ThemeProvider } from "@/hooks/useTheme";
import Index from "./pages/Index";
import Insights from "./pages/Insights";
import InsightArticle from "./pages/InsightArticle";
import SentinelHubGuard from "./pages/SentinelHubGuard";
import DivulgacaoResponsavel from "./pages/DivulgacaoResponsavel";
import NotFound from "./pages/NotFound";
import ThreatMap from "./pages/ThreatMap";

const queryClient = new QueryClient();

const LegacyArticleRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/pt-br/insights/${slug}`} replace />;
};

const LANGS = ["pt-br", "en", "es"] as const;

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <LanguageProvider>
              <Routes>
                {/* Redirects from legacy paths */}
                <Route path="/" element={<Navigate to="/pt-br/" replace />} />
                <Route path="/SentinelHub" element={<Navigate to="/pt-br/" replace />} />
                <Route path="/sentinelhub" element={<Navigate to="/pt-br/" replace />} />
                <Route path="/insights" element={<Navigate to="/pt-br/insights" replace />} />
                <Route path="/insights/:slug" element={<LegacyArticleRedirect />} />
                <Route path="/divulgacao-responsavel" element={<Navigate to="/pt-br/divulgacao-responsavel" replace />} />

                {/* Language-prefixed routes */}
                {LANGS.map((l) => (
                  <Route key={l} path={`/${l}`} element={<Index />} />
                ))}
                {LANGS.map((l) => (
                  <Route key={`${l}-slash`} path={`/${l}/`} element={<Index />} />
                ))}
                {LANGS.map((l) => (
                  <Route key={`${l}-ins`} path={`/${l}/insights`} element={<Insights />} />
                ))}
                {LANGS.map((l) => (
                  <Route key={`${l}-art`} path={`/${l}/insights/:slug`} element={<InsightArticle />} />
                ))}

                {/* Localized disclosure pages */}
                <Route path="/pt-br/divulgacao-responsavel" element={<DivulgacaoResponsavel />} />
                <Route path="/en/responsible-disclosure" element={<DivulgacaoResponsavel />} />
                <Route path="/es/divulgacion-responsable" element={<DivulgacaoResponsavel />} />

                {/* Threat Map (localized) */}
                <Route path="/pt-br/mapa-de-ameacas" element={<ThreatMap />} />
                <Route path="/en/threat-map" element={<ThreatMap />} />
                <Route path="/es/mapa-de-amenazas" element={<ThreatMap />} />

                {/* Standalone (no language prefix) */}
                <Route path="/sentinelhubguard" element={<SentinelHubGuard />} />

                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </LanguageProvider>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
